import React from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import * as Sentry from '@sentry/browser'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { flashErrorMessage } from 'redux-flash'
import { Path, isInheritedIraEnabled } from 'config/portal'
import {
  NO_SERVICES_AGREEMENT_OPTION,
  RolloverType,
  RolloverServices,
} from 'config/automatic-rollovers'
import { TOAST_ERROR_CONTACT_SUPPORT } from 'config/notifications'
import {
  formatCurrency,
  formatFileName,
  useParticipantsSummary,
  downloadDiscardedParticipants,
} from 'utils'
import { useHistory } from 'react-router-dom'
import { FlowActions } from '../../products/components'
import { ParticipantsSummaryTable } from '../components'
import { sum, some, map } from 'lodash'
import { selectors as apiSelectors } from 'lp-redux-api'
import { RecordTypes } from 'flatfile-config'
import SharedReviewAndSubmitView from '../../products/views/ReviewAndSubmitView'

const propTypes = {
  participantsAgreementHash: PropTypes.object,
  participantFileName: PropTypes.string,
  createParticipantsData: PropTypes.func.isRequired,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  discardedParticipants: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  isSubmittingParticipantsData: PropTypes.bool.isRequired,
  rolloverType: PropTypes.string,
}

const defaultProps = {
  participantsAgreementHash: null,
  participantFileName: null,
}

const FILE_NAME_PREFIX = 'RSP_'
const FILE_NAME_MANUAL_ENTRY = 'RSP_Inspira_Financial_Manual_Entry.xlsx'

const scrubIraAmount = (val) => val.replaceAll(/[$]/g, '')
const DISCARDED_PARTICIPANTS_TEXT =
  'Some participants will not be included in your submission due to issues with those participant\u2019s data, their plan type and status, agreement status, or their rollover amount. Please download the data for these participants for your records and resubmit them after the issues have been resolved.'

function ReviewAndSubmitView({
  participantsAgreementHash,
  participantFileName,
  createParticipantsData,
  flashErrorMessageHandler,
  participantsForSubmission,
  discardedParticipants,
  isSubmittingParticipantsData,
  rolloverType,
}) {
  const history = useHistory()
  const {
    data: participantsSummaryData,
    overview: participantsSummaryOverview,
  } = useParticipantsSummary(participantsForSubmission)

  const isInheritedIraRollover =
    rolloverType === RolloverType.INHERITED_IRA && isInheritedIraEnabled()

  const getRolloverServiceType = () => {
    if (rolloverType === RolloverType.STANDARD) {
      return RolloverServices.STANDARD
    }
    if (isInheritedIraRollover) {
      return RolloverServices.INHERITED_IRA
    }
    return RolloverServices.UNCASHED_CHECKS
  }

  const rolloverServiceType = getRolloverServiceType()

  const handleSubmit = async () => {
    const isPartialSubmission = some(
      participantsAgreementHash,
      ({ servicesAgreement }) =>
        servicesAgreement === NO_SERVICES_AGREEMENT_OPTION.value
    )
    // scrub the '$' currency from IRA amounts before submission
    const scrubbedParticipantsForSubmission =
      rolloverType === RolloverType.STANDARD ||
      rolloverType === RolloverType.INHERITED_IRA
        ? map(
            //For ARO
            participantsForSubmission,
            ({
              traditionalIraAfterTaxAmount,
              traditionalIraPreTaxAmount,
              rothIraAmount,
              ...rest
            }) => ({
              ...rest,
              traditionalIraAfterTaxAmount: scrubIraAmount(
                traditionalIraAfterTaxAmount
              ),
              traditionalIraPreTaxAmount: scrubIraAmount(
                traditionalIraPreTaxAmount
              ),
              rothIraAmount: scrubIraAmount(rothIraAmount),
            })
          )
        : map(
            //FOR Uncashed Checks
            participantsForSubmission,
            ({ traditionalIraPreTaxAmount, rothIraAmount, ...rest }) => ({
              ...rest,
              traditionalIraPreTaxAmount: scrubIraAmount(
                traditionalIraPreTaxAmount
              ),
              rothIraAmount: scrubIraAmount(rothIraAmount),
            })
          )

    try {
      await createParticipantsData({
        fileName: participantFileName
          ? formatFileName(participantFileName, FILE_NAME_PREFIX)
          : FILE_NAME_MANUAL_ENTRY,
        participants: scrubbedParticipantsForSubmission,
        summary: {
          totalPlans: participantsSummaryOverview.totalPlans,
          totalParticipants: participantsSummaryOverview.totalParticipants,
          totalRolloverAmount:
            rolloverType === RolloverType.UNCASHED_CHECKS
              ? formatCurrency(
                  sum([
                    participantsSummaryOverview.traditionalIraPreTaxAmount,
                    participantsSummaryOverview.rothIraAmount,
                  ])
                )
              : formatCurrency(
                  sum([
                    participantsSummaryOverview.traditionalIraPreTaxAmount,
                    participantsSummaryOverview.traditionalIraAfterTaxAmount,
                    participantsSummaryOverview.rothIraAmount,
                  ])
                ),
        },
        serviceType: rolloverServiceType,
      })

      history.push({
        pathname: Path.SERVICES_AGREEMENTS,
        state: {
          ignorePrompt: true,
          showAroSuccessModal: true,
          isPartialSubmission,
        },
      })
    } catch (e) {
      Sentry.captureException(e)
      flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    }
  }

  const handleDownload = () => {
    downloadDiscardedParticipants({
      participants: discardedParticipants,
      originalFileName: participantFileName,
      recordType: RecordTypes.ARO_PARTICIPANT,
    })
  }

  return (
    <SharedReviewAndSubmitView
      participantsAgreementHash={participantsAgreementHash}
      participantsForSubmission={participantsForSubmission}
      discardedParticipants={discardedParticipants}
      isSubmittingParticipantsData={isSubmittingParticipantsData}
      onSubmit={handleSubmit}
      onDownload={handleDownload}
      uploadFilePath="/automatic-rollovers/upload-file"
      summaryData={participantsSummaryData}
      SummaryTableComponent={ParticipantsSummaryTable}
      FlowActionsComponent={FlowActions}
      summaryTableProps={{ rolloverType }}
      discardedParticipantsText={DISCARDED_PARTICIPANTS_TEXT}
    />
  )
}

ReviewAndSubmitView.propTypes = propTypes
ReviewAndSubmitView.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    participantFileName: selectors.participantFileName(state),
    participantsAgreementHash: selectors.participantsAgreementHash(state),
    participantsForSubmission: selectors.participantsForSubmission(state),
    discardedParticipants: selectors.discardedParticipants(state),
    isSubmittingParticipantsData: apiSelectors.isLoading(
      state,
      apiActions.createParticipantsData
    ),
    rolloverType: selectors.rolloverType(state),
  }
}

const mapDispatchToProps = {
  createParticipantsData: apiActions.createParticipantsData,
  flashErrorMessageHandler: flashErrorMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ReviewAndSubmitView
)
